import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * /magic-login?token=<raw_token>
 *
 * Consumes the magic link token on mount by POSTing to the backend. Any
 * link-preview bot that only GETs the URL cannot consume the token —
 * consumption is POST-only.
 */
export default function MagicLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setErrorMsg("Missing login token.");
      setStatus("error");
      return;
    }

    const verify = async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/magic-link/verify`,
          { token },
          { withCredentials: true }
        );
        // Store the session_token so axios interceptor picks it up on
        // subsequent requests (identical to the Google auth flow).
        if (data?.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        if (data?.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        navigate("/jobs", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setErrorMsg(
          detail === "invalid_or_expired"
            ? "This login link has expired or was already used. Please request a new one."
            : "We couldn't log you in. Please request a new link."
        );
        setStatus("error");
      }
    };
    verify();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <img
          src="https://customer-assets.emergentagent.com/job_window-screen-mgmt/artifacts/xq82sr3z_ScreenzZ-1024x1024.png"
          alt="ScreenzZ Logo"
          className="h-24 mx-auto mb-6"
        />

        {status === "verifying" && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-zinc-200 border-t-[#207024] mx-auto mb-6"></div>
            <p className="font-inter text-zinc-700" data-testid="magic-login-verifying">
              Logging you in&hellip;
            </p>
          </>
        )}

        {status === "error" && (
          <div data-testid="magic-login-error" className="space-y-4">
            <div className="text-red-600 font-inter font-medium">{errorMsg}</div>
            <button
              onClick={() => navigate("/login", { replace: true })}
              data-testid="magic-login-back-button"
              className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold shadow-md"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
